import { FirebaseError } from 'firebase/app';
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  reload,
  sendEmailVerification,
  updatePassword,
  type User,
} from 'firebase/auth';

export async function resendEmailVerification(user: User): Promise<void> {
  await sendEmailVerification(user);
}

export async function refreshEmailVerification(user: User): Promise<boolean> {
  await reload(user);

  if (user.emailVerified) {
    // Refresh the ID token too, so downstream rules/functions immediately see
    // the new email_verified claim when the application mounts.
    await user.getIdToken(true);
  }

  return user.emailVerified;
}

export async function changePasswordWithReauthentication(
  user: User,
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  if (!user.email) {
    throw new Error('Password users must have an email address.');
  }

  const credential = EmailAuthProvider.credential(user.email, currentPassword);
  await reauthenticateWithCredential(user, credential);
  await updatePassword(user, newPassword);
}

export async function reauthenticateForAccountDeletion(
  user: User,
  currentPassword?: string,
): Promise<void> {
  const providerIds = new Set(user.providerData.map((provider) => provider.providerId));
  if (providerIds.has('password')) {
    if (!user.email || !currentPassword) throw new Error('Current password is required.');
    await reauthenticateWithCredential(
      user,
      EmailAuthProvider.credential(user.email, currentPassword),
    );
    return;
  }
  if (providerIds.has('google.com')) {
    await reauthenticateWithPopup(user, new GoogleAuthProvider());
    return;
  }
  if (providerIds.has('apple.com')) {
    await reauthenticateWithPopup(user, new OAuthProvider('apple.com'));
    return;
  }
  throw new Error('This authentication provider cannot confirm account deletion.');
}

export function getPasswordResetErrorKey(error: unknown): string {
  if (error instanceof FirebaseError) {
    switch (error.code) {
      // Keep reset responses account-enumeration neutral.
      case 'auth/user-not-found':
        return '';
      case 'auth/invalid-email':
        return 'login.invalidResetEmail';
      case 'auth/too-many-requests':
        return 'login.tooManyRequests';
      case 'auth/network-request-failed':
        return 'login.networkError';
      default:
        return 'login.passwordResetError';
    }
  }

  return 'login.passwordResetError';
}

export function getPasswordChangeErrorKey(error: unknown): string {
  if (error instanceof FirebaseError) {
    switch (error.code) {
      case 'auth/invalid-credential':
      case 'auth/wrong-password':
        return 'profile.wrongCurrentPassword';
      case 'auth/weak-password':
        return 'profile.passwordRequirements';
      case 'auth/too-many-requests':
        return 'profile.tooManyPasswordAttempts';
      case 'auth/network-request-failed':
        return 'profile.passwordNetworkError';
      default:
        return 'profile.passwordUpdateError';
    }
  }

  return 'profile.passwordUpdateError';
}
